// `ForgotPasswordPage` — US-005.
//
// DESIGN.md §1.5, §4.5: anti-enumeration UX — both success AND 4xx replace
// the form with the same banner. Only true transport faults (network, 5xx)
// surface as a negative banner with the form intact.

import { useId, useState } from 'react';

import { ApiError, useForgotPassword } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Field, Input } from '@barel/ui';
import { Link } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { AuthLayout } from './AuthLayout';
import { fieldError } from './lib/localizeError';
import { forgotPasswordFormSchema, type ForgotPasswordFormInput } from './lib/schemas';
import { emit } from './lib/telemetry';

export function ForgotPasswordPage() {
  const t = useT('auth');
  const forgot = useForgotPassword();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<ForgotPasswordFormInput>({
    resolver: zodResolver(forgotPasswordFormSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState<boolean>(false);

  const onSubmit: SubmitHandler<ForgotPasswordFormInput> = async (values) => {
    setBannerError(null);
    setRateLimited(false);
    emit({ name: 'auth_forgot_submitted' });
    try {
      // `redirectTo` becomes the reset link's `callbackURL`. Better Auth
      // rejects an EMPTY callbackURL with INVALID_TOKEN, so omitting this
      // makes every emailed reset link a dead end.
      await forgot.mutateAsync({ email: values.email, redirectTo: '/auth/reset' });
      setSubmitted(true);
    } catch (err) {
      if (ApiError.is(err)) {
        if (err.httpStatus === 429 || err.code === 'auth.rate_limited') {
          setRateLimited(true);
          return;
        }
        // 4xx other than 429 → treat as anti-enum success (per DESIGN §4.5).
        if (err.httpStatus >= 400 && err.httpStatus < 500) {
          setSubmitted(true);
          return;
        }
      }
      // 5xx / network — keep form intact.
      setBannerError(t('resetPassword.request.errors.generic'));
    }
  };

  const titleId = useId();
  const emailError = fieldError(errors.email, t('fieldErrors.email.invalid'));

  return (
    <AuthLayout brandHeadline="brandHeadlineNeutral">
      <section data-route="auth/forgot" aria-labelledby={titleId} className="flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <h1 id={titleId} className="text-xl font-semibold text-primary">
            {t('resetPassword.request.title')}
          </h1>
          <p className="text-sm text-secondary">{t('resetPassword.request.subtitle')}</p>
        </header>

        {submitted ? (
          <div className="flex flex-col gap-4" data-testid="forgot-anti-enum">
            <Banner tone="info">{t('resetPassword.request.antiEnum')}</Banner>
            <Button asChild variant="primary" size="lg" fullWidth>
              <a href={`mailto:${getValues('email') ?? ''}`}>
                {t('resetPassword.request.openInbox')}
              </a>
            </Button>
            <Link to="/auth/login" className="text-sm text-secondary underline hover:text-primary">
              {t('resetPassword.request.backToSignIn')}
            </Link>
          </div>
        ) : (
          <>
            {rateLimited ? (
              <Banner tone="warning">{t('resetPassword.request.errors.rateLimited')}</Banner>
            ) : null}
            {bannerError ? <Banner tone="error">{bannerError}</Banner> : null}

            <form
              aria-busy={isSubmitting || undefined}
              className="flex flex-col gap-4"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              data-testid="forgot-form"
            >
              <Field invalid={!!emailError} required>
                <Field.Label>{t('resetPassword.request.fields.email.label')}</Field.Label>
                <Field.Control>
                  <Input
                    {...register('email')}
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder={t('resetPassword.request.fields.email.placeholder')}
                    data-testid="forgot-email"
                  />
                </Field.Control>
                {emailError ? <Field.Error>{emailError}</Field.Error> : null}
              </Field>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={isSubmitting}
                data-testid="forgot-submit"
              >
                {t('resetPassword.request.submit')}
              </Button>

              <Link
                to="/auth/login"
                className="text-sm text-secondary underline hover:text-primary"
              >
                {t('resetPassword.request.backToSignIn')}
              </Link>
            </form>
          </>
        )}
      </section>
    </AuthLayout>
  );
}
